// Heatmap strip + per-question failure breakdown for one standard.
// `step.historicalScores` is populated when calculateSimulation is called with
// allStandardsData; without it the trend dots are skipped.
import { useTranslation } from "react-i18next";

const cellColor = (score) => {
  if (score < 0.1) return "bg-emerald-100 text-emerald-800";
  if (score < 0.2) return "bg-lime-100 text-lime-800";
  if (score < 0.3) return "bg-amber-100 text-amber-800";
  if (score < 0.4) return "bg-orange-100 text-orange-800";
  if (score < 0.5) return "bg-orange-200 text-orange-900";
  if (score < 0.6) return "bg-rose-400 text-white";
  if (score < 0.7) return "bg-rose-500 text-white";
  return "bg-rose-600 text-white";
};

const dotColor = (score) => {
  if (score < 0.1) return "bg-emerald-500";
  if (score < 0.2) return "bg-lime-500";
  if (score < 0.3) return "bg-amber-500";
  if (score < 0.5) return "bg-orange-500";
  return "bg-rose-500";
};

const severityText = (score) => {
  if (score > 0.4) return "text-rose-600 font-bold";
  if (score > 0.2) return "text-orange-600 font-semibold";
  return "text-emerald-600 font-medium";
};

export default function BottleneckHeatmap({ data, onCellClick }) {
  const { t } = useTranslation();
  // Aggregated scopes pass no handler — the counts have no teacher rows behind
  // them — so the pointer and the "click to see who failed" tip stay off.
  const canDrill = Boolean(onCellClick);
  return (
    <div className="flex flex-col h-full p-4 overflow-y-auto">
      <div className="flex w-full h-24 shrink-0 rounded-lg overflow-hidden border border-slate-200 shadow-sm mb-2">
        {data.map((step) => {
          // A question nobody in the pool was asked has no failure rate.
          const isInactive = step.askedCount === 0;
          const colorClass = isInactive
            ? "bg-gray-200 text-gray-500"
            : cellColor(step.weakestIndicatorScore);
          const percent = Math.round(step.weakestIndicatorScore * 100);
          const title = isInactive
            ? `${step.shortLabel}: ${t("standards.noTeachersEvaluatedTip")}`
            : `${step.shortLabel}: ${t("standards.failureRateTip", { pct: percent })}`;
          const displayValue = isInactive ? "–" : `${percent}%`;

          return (
            <div
              key={step.stepIndex}
              className={`flex-1 flex flex-col items-center justify-center border-r border-white/20 last:border-r-0 transition-all duration-300 ${colorClass} ${
                !isInactive && canDrill && "cursor-pointer hover:scale-105 hover:z-10"
              } relative group/cell`}
              title={title}
              onClick={() => !isInactive && onCellClick?.(step.stepIndex)}
            >
              <span className="text-lg font-bold">{displayValue}</span>

              {!isInactive && step.historicalScores && (
                <div className="absolute bottom-2 flex gap-1">
                  {step.historicalScores.map((hScore, hIdx) => (
                    <div
                      key={hIdx}
                      className={`w-2 h-2 rounded-full border border-white/30 shadow-sm ${dotColor(hScore)}`}
                      title={t("standards.observationDropTip", { n: hIdx + 1, pct: Math.round(hScore * 100) })}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex w-full mb-6 text-xs text-slate-500 text-center shrink-0">
        {data.map((step) => (
          <div key={step.stepIndex} className="flex-1 px-1 truncate">
            {step.shortLabel}
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">
          {t("standards.weakPointBreakdown")}
        </h4>
        {data.map((step) => {
          const isInactive = step.askedCount === 0;
          return (
            <div
              key={step.stepIndex}
              className="flex items-start justify-between gap-4 group"
            >
              <div className="flex flex-col">
                <span className="text-sm text-slate-700 leading-tight group-hover:text-blue-600 transition-colors">
                  {step.label}
                </span>
                <span className="text-[10px] text-slate-500 mt-0.5">
                  {t("standards.enteredFailed", { entered: step.askedCount, failed: step.failCount })}
                </span>
              </div>
              <div
                className={`text-right whitespace-nowrap ${
                  isInactive
                    ? "text-gray-400"
                    : severityText(step.weakestIndicatorScore)
                } ${!isInactive && canDrill && "cursor-pointer"}`}
                onClick={() => !isInactive && onCellClick?.(step.stepIndex)}
                title={
                  isInactive
                    ? t("standards.noTeachersEvaluatedTip")
                    : canDrill
                    ? t("standards.clickToSeeFailed")
                    : undefined
                }
              >
                {isInactive
                  ? t("standards.na")
                  : t("standards.dropShort", { pct: Math.round(step.weakestIndicatorScore * 100) })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
